import { useClickOutside } from '../../hooks/useClickOutside';
import { useDraggable } from '../../hooks/useDraggable';
import { useMergedRefs } from '../../hooks/useMergedRefs';
import { GripVertical, ChevronDown, PhoneOutgoing, PhoneIncoming } from 'lucide-react';
import {
    RenderCallButtons,
    QualificationBadges,
    RenderSoundWarning,
    CallTranscript,
    UpdateContactStatus,
} from './components';
import { useAccordion } from '../../hooks/useAccordion';
import { useGetCallbackHealth } from 'services/hooks/queries';
import { Tooltip, Button, Chip } from 'common/components';
import { CallStatuses } from 'contexts/LiveKitContext';
import { useNavigate } from 'react-router';
import { UseAgentCallConsoleReturn } from './hooks/useAgentCallConsole';

import styles from './AgentCallConsole.module.css';

export type AgentCallConsoleProps = UseAgentCallConsoleReturn & {
    onClose: () => void;
    isOpen: boolean;
};

export const AgentCallConsole = ({ onClose, isOpen, ...rest }: AgentCallConsoleProps) => {
    const navigate = useNavigate();
    const {
        room,
        // LiveKit state
        callStatus,
        agentAvailable,
        callTranscript,
        callDetails,
        availableStatuses,
        clientInfo,
        qualifications,

        // Local state
        isMuted,
        isOnHold,
        showAudioPrompt,

        // Call control functions
        handleMuteToggle,
        handleHoldToggle,
        handleToggle,
        handleAcceptCall,
        handleRejectCall,
        handleHangupCall,

        // Audio prompt control
        setShowAudioPrompt,
    } = rest;

    const isCallActive = callStatus === CallStatuses.IN_PROGRESS || callStatus === CallStatuses.INCOMING;
    const isCallInProgress = callStatus === CallStatuses.IN_PROGRESS;
    const isManualCallbackTriggered = callStatus === CallStatuses.MANUAL_CALLBACK;
    const hasCallInfo =
        !!qualifications?.length || !!callDetails || !!availableStatuses?.length || !!callTranscript?.length;
    // Safe check: Only allow hangup if connected to LiveKit room
    const isConnectedToLiveKit = room?.state === 'connected' && !!room?.localParticipant?.sid;

    // Only allow closing via click outside when no call is active
    const handleClose = () => {
        if (!isCallActive) {
            onClose();
        }
    };

    const clickOutsideRef = useClickOutside(handleClose, isOpen);

    const accordion = useAccordion({
        defaultOpen: 'section1',
    });

    // Use the draggable hook
    const { isDragging, elementRef, handleMouseDown, dragStyle, dragHandleStyle } = useDraggable<HTMLButtonElement>({
        x: 0,
        y: 80,
    });

    // Merge the refs properly
    const mergedRef = useMergedRefs(clickOutsideRef, elementRef);

    const { callbackHealthData } = useGetCallbackHealth();

    return (
        <div
            ref={mergedRef}
            data-testid='call-console'
            className={`${styles.dashboardAgentConsole}  ${isOpen ? styles.open : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                ...dragStyle,
                position: 'absolute',
                zIndex: 1001,
            }}
        >
            <div className={styles.dashboardAgentConsoleHeader}>
                <div className={styles.dashboardAgentConsoleGroup}>
                    <button className='button-clear' onMouseDown={handleMouseDown} style={dragHandleStyle}>
                        <GripVertical />
                    </button>
                    <h4>Call Console</h4>
                    <div className='active-toggle'>
                        <label className='agent-toggle-switch'>
                            <input type='checkbox' checked={agentAvailable} onChange={handleToggle} />
                            <span className='agent-toggle-slider round'></span>
                        </label>
                        <span>{agentAvailable ? 'Active' : 'Inactive'}</span>
                    </div>
                    {clientInfo.isParentCall && <Chip>Parent Call</Chip>}
                </div>

                {isCallActive ? (
                    <div className='client-info-container-wrapper'>
                        {clientInfo.isParentCall && (
                            <div className='client-info-container'>
                                <span className='text-small'>Call Date</span>
                                <span className='text-bold'>{clientInfo.parentCallDate}</span>
                            </div>
                        )}
                        <div className='client-info-container'>
                            <span className='text-small'>Name</span>
                            <span className='text-bold'>{clientInfo.name}</span>
                        </div>
                        {clientInfo.callDirection && (
                            <div className='client-info-container'>
                                <span className='text-small'>Call Direction</span>
                                <span className='text-bold capitalize'>{clientInfo.callDirection.toLowerCase()}</span>
                            </div>
                        )}
                        <div className='client-info-container'>
                            <span className='text-small'>Phone Number</span>
                            <span className='text-bold flex gap-sm items-center'>
                                {clientInfo.callDirection === 'OUTBOUND' && (
                                    <Tooltip content='Outbound Call' position='bottom'>
                                        <PhoneOutgoing size={14} color='#9b51e0' />
                                    </Tooltip>
                                )}
                                {clientInfo.callDirection === 'INBOUND' && (
                                    <Tooltip content='Inbound Call' position='bottom'>
                                        <PhoneIncoming size={14} color='#13ce64' />
                                    </Tooltip>
                                )}
                                {clientInfo.phoneNumber}
                            </span>
                        </div>
                        <div className='client-info-container'>
                            <span className='text-small'>Workflow</span>
                            <span className='text-bold'>{clientInfo.workflow}</span>
                        </div>
                    </div>
                ) : (
                    <div className='client-info-container-wrapper'>
                        {isManualCallbackTriggered ? (
                            <span>Callback initiated...</span>
                        ) : (
                            <>
                                {callbackHealthData?.pendingTotal ? (
                                    <div>
                                        <span>{callbackHealthData.pendingTotal} callbacks available - </span>
                                        <Button
                                            variant='link'
                                            onClick={() => navigate('/agent-command-center')}
                                            size='lg'
                                        >
                                            Click to view
                                        </Button>
                                    </div>
                                ) : (
                                    <Button variant='link' onClick={() => navigate('/agent-command-center')} size='lg'>
                                        Agent Command Center
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                )}

                <RenderCallButtons
                    callStatus={callStatus}
                    onAcceptCall={handleAcceptCall}
                    onRejectCall={handleRejectCall}
                    onHangupCall={handleHangupCall}
                    isMuted={isMuted}
                    isOnHold={isOnHold}
                    onMuteToggle={handleMuteToggle}
                    onHoldToggle={handleHoldToggle}
                    isConnectedToLiveKit={isConnectedToLiveKit}
                />

                {isCallInProgress && hasCallInfo && (
                    <button className='button-clear' {...accordion.getTriggerProps('section1')}>
                        <ChevronDown />
                    </button>
                )}
            </div>

            {isCallInProgress && hasCallInfo && (
                <div className={styles.dashboardAgentConsoleBody} {...accordion.getContentProps('section1')}>
                    <div className={styles.dashboardAgentConsoleGroupVertical}>
                        <QualificationBadges qualifications={qualifications} />

                        <div className={styles.dashboardUpdateContactStatus}>
                            <UpdateContactStatus statuses={availableStatuses} contactId={callDetails?.id} />
                        </div>
                    </div>
                    <CallTranscript messages={callTranscript} />
                </div>
            )}

            <RenderSoundWarning
                showAudioPrompt={showAudioPrompt}
                callStatus={callStatus}
                setShowAudioPrompt={setShowAudioPrompt}
            />
        </div>
    );
};
